import { useEffect, useState } from "react";
import { Link, useParams, useNavigate, useLocation } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { SharePreviewFrame } from "@/components/builder/SharePreviewFrame";
import { LinkHealthDialog } from "@/components/builder/LinkHealthDialog";
import { HygieneCheckDialog } from "@/components/builder/HygieneCheckDialog";
import { runLinkHealthCheck } from "@/lib/linkHealth";
import { runHygieneCheck } from "@/lib/hygieneCheck";
import { shareToX, shareToWhatsApp, shareToInstagram } from "@/lib/socialShare";
import { toast } from "sonner";

export default function PageShare() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [page, setPage] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [checkingLinks, setCheckingLinks] = useState(false);
  const [autoPromptOpen, setAutoPromptOpen] = useState(false);
  const [healthDialog, setHealthDialog] = useState({ open: false, brokenLinks: [] });
  const [hygieneDialog, setHygieneDialog] = useState({ open: false, mismatches: [] });

  useEffect(() => {
    api.get(`/pages/${id}`).then(({ data }) => setPage(data)).catch(() => setNotFound(true));
  }, [id]);

  useEffect(() => {
    if (page && location.state?.promptPublish && page.status !== "published") {
      setAutoPromptOpen(true);
    }
  }, [page, location.state]);

  if (notFound) {
    return (
      <div className="min-h-screen bg-[#0B0E14] flex items-center justify-center">
        <p className="text-slate-400">Page not found.</p>
      </div>
    );
  }

  if (!page) {
    return (
      <div className="min-h-screen bg-[#0B0E14] flex items-center justify-center">
        <div className="h-8 w-8 rounded-full border-2 border-indigo-400 border-t-transparent animate-spin" />
      </div>
    );
  }

  const title = page.type === "event" ? page.event_name || "Untitled Event" : page.title || "Untitled Release";
  const publicUrl = page.url_handle ? `${window.location.origin}/${page.url_handle}/${page.slug}` : `${window.location.origin}/s/${page.slug}`;
  const shareText = `Check out "${title}"`;

  const handleCopy = () => {
    navigator.clipboard.writeText(publicUrl);
    toast.success(page.status === "published" ? "Link copied" : "Link copied — remember to publish to make it live!");
  };

  const handleViewPage = () => {
    if (page.status === "published") {
      window.open(publicUrl, "_blank");
    } else {
      window.open(`${window.location.origin}/preview/${page.id}`, "_blank");
    }
  };

  const doPublish = async () => {
    setPublishing(true);
    try {
      const { data } = await api.post(`/pages/${id}/publish`, { status: "published" });
      setPage(data);
      toast.success("Page published! Your SmartLinks page is now live.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not publish page");
    } finally {
      setPublishing(false);
    }
  };

  const handlePublishNow = async () => {
    setAutoPromptOpen(false);
    setCheckingLinks(true);
    const broken = await runLinkHealthCheck(id);
    if (broken.length > 0) {
      setCheckingLinks(false);
      setHealthDialog({ open: true, brokenLinks: broken });
      return;
    }
    if (page.type === "event") {
      const mismatches = await runHygieneCheck(id);
      setCheckingLinks(false);
      if (mismatches.length > 0) {
        setHygieneDialog({ open: true, mismatches });
        return;
      }
    } else {
      setCheckingLinks(false);
    }
    await doPublish();
  };

  const handlePublishAnyway = async () => {
    setHealthDialog({ open: false, brokenLinks: [] });
    await doPublish();
  };

  const handleHygienePublishAnyway = async () => {
    setHygieneDialog({ open: false, mismatches: [] });
    await doPublish();
  };

  const handleKeepDraft = () => setAutoPromptOpen(false);

  const handleShareInstagram = () => {
    const message = shareToInstagram(publicUrl);
    toast.info(message);
  };

  return (
    <div className="min-h-screen bg-[#0B0E14] text-white flex items-center justify-center px-6 py-10" data-testid="page-share-screen">
      <div className="w-full max-w-md space-y-6 text-center">
        <div className="space-y-2">
          <i className="fa-solid fa-circle-check text-emerald-400 text-3xl" />
          <h1 className="text-2xl font-bold font-display">Your SmartLinks page is saved!</h1>
          <p className="text-sm text-slate-400" data-testid="share-status-message">
            {page.status === "published" ? "It's live and ready to share." : "It's saved as a draft — publish it below to make it live."}
          </p>
        </div>

        <SharePreviewFrame page={page} />

        <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 text-left">
          <p className="font-semibold truncate" data-testid="share-page-title">{title}</p>
          <p className="text-xs text-slate-400 font-mono-slug truncate" data-testid="share-page-url">{publicUrl}</p>
        </div>

        {page.status !== "published" && (
          <Button
            onClick={handlePublishNow}
            disabled={publishing || checkingLinks}
            className="w-full bg-emerald-500 hover:bg-emerald-600 rounded-full h-11 font-bold"
            data-testid="share-publish-now-button"
          >
            <i className="fa-solid fa-bolt mr-2" />
            {checkingLinks ? "Checking..." : publishing ? "Publishing..." : "Publish Now — Go Live"}
          </Button>
        )}

        <div className="flex items-center justify-center gap-3">
          <button
            onClick={() => shareToWhatsApp(publicUrl, shareText)}
            className="w-11 h-11 rounded-full bg-[#25D366]/15 text-[#25D366] flex items-center justify-center hover:bg-[#25D366]/25 transition-colors"
            title="Share on WhatsApp"
            data-testid="share-whatsapp-button"
          >
            <i className="fa-brands fa-whatsapp text-lg" />
          </button>
          <button
            onClick={() => shareToX(publicUrl, shareText)}
            className="w-11 h-11 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-white/20 transition-colors"
            title="Share on X"
            data-testid="share-x-button"
          >
            <i className="fa-brands fa-x-twitter text-lg" />
          </button>
          <button
            onClick={handleShareInstagram}
            className="w-11 h-11 rounded-full bg-gradient-to-tr from-amber-500/20 via-pink-500/20 to-purple-500/20 text-pink-300 flex items-center justify-center hover:brightness-125 transition-all"
            title="Share on Instagram"
            data-testid="share-instagram-button"
          >
            <i className="fa-brands fa-instagram text-lg" />
          </button>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <Button onClick={handleCopy} variant="outline" className="flex-1 border-white/15 hover:bg-white/10 rounded-full h-11" data-testid="share-copy-link-button">
            <i className="fa-solid fa-copy mr-2" /> Copy Link
          </Button>
          <Button onClick={handleViewPage} className="flex-1 bg-indigo-500 hover:bg-indigo-600 rounded-full h-11" data-testid="share-view-page-button">
            <i className="fa-solid fa-arrow-up-right-from-square mr-2" /> View Landing Page
          </Button>
        </div>

        {page.status === "published" && page.og_image_url && (
          <div className="rounded-2xl border border-white/10 p-4 space-y-3" data-testid="share-card-block">
            <img src={fileUrl(page.og_image_url)} alt="Share card preview" className="w-full rounded-lg border border-white/10" data-testid="share-card-preview-image" />
            <a href={fileUrl(page.og_image_url)} download={`${page.slug}-share-card.jpg`} data-testid="share-card-download-link">
              <Button variant="outline" className="w-full border-white/15 hover:bg-white/10 rounded-full h-11">
                <i className="fa-solid fa-download mr-2" /> Download Share Card
              </Button>
            </a>
            <p className="text-xs text-slate-500 text-center">Auto-generated from your cover art & title — perfect for Instagram Stories or manual posts.</p>
          </div>
        )}

        <div className="flex items-center justify-center gap-4 text-sm pt-2">
          <button onClick={() => navigate(`/builder/${id}`)} className="text-indigo-400 font-semibold hover:text-indigo-300" data-testid="share-continue-editing-link">
            Continue Editing
          </button>
          <span className="text-white/20">·</span>
          <Link to="/dashboard" className="text-slate-400 hover:text-white" data-testid="share-back-to-dashboard-link">
            Back to Dashboard
          </Link>
        </div>
      </div>

      <Dialog open={autoPromptOpen} onOpenChange={(o) => !o && handleKeepDraft()}>
        <DialogContent className="bg-[#121824] border-white/10 text-white" data-testid="auto-publish-prompt-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">
              <i className="fa-solid fa-rocket mr-2 text-indigo-400" />Ready to go live?
            </DialogTitle>
            <DialogDescription>
              Your page "{title}" is saved. Publish it now so fans can start clicking, or keep it as a draft and publish later.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={handleKeepDraft} className="border-white/15 hover:bg-white/10" data-testid="auto-publish-keep-draft-button">
              Keep as Draft
            </Button>
            <Button
              onClick={handlePublishNow}
              disabled={checkingLinks || publishing}
              className="bg-emerald-500 hover:bg-emerald-600 text-black font-bold"
              data-testid="auto-publish-now-button"
            >
              {checkingLinks ? "Checking..." : publishing ? "Publishing..." : "Publish Now"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <LinkHealthDialog
        open={healthDialog.open}
        brokenLinks={healthDialog.brokenLinks}
        onCancel={() => setHealthDialog({ open: false, brokenLinks: [] })}
        onPublishAnyway={handlePublishAnyway}
        publishing={publishing}
      />

      <HygieneCheckDialog
        open={hygieneDialog.open}
        mismatches={hygieneDialog.mismatches}
        onCancel={() => setHygieneDialog({ open: false, mismatches: [] })}
        onPublishAnyway={handleHygienePublishAnyway}
        publishing={publishing}
      />
    </div>
  );
}
